// src/components/common/CartDrawer.jsx
// Slides in from the right — desktop only. On mobile, /cart page is used instead.
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";

const IconX = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);
const IconCart = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  </svg>
);
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

const CSS = `
/* ── OVERLAY ── */
.cdrawer-overlay {
  position: fixed; inset: 0; z-index: 1300;
  background: rgba(0,0,0,0); /* starts transparent */
  transition: background 0.3s ease;
  pointer-events: none;
}
.cdrawer-overlay.open {
  background: rgba(0,0,0,0.6);
  pointer-events: all;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

/* ── DRAWER PANEL ── */
.cdrawer {
  position: fixed;
  top: 0; right: 0; bottom: 0;
  width: 420px;
  max-width: 100vw;
  z-index: 1400;
  background: #0f0f0f;
  border-left: 1px solid rgba(255,255,255,0.07);
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 0.32s cubic-bezier(0.32, 0.72, 0, 1);
  box-shadow: -24px 0 80px rgba(0,0,0,0.6);
}
.cdrawer.open {
  transform: translateX(0);
}

/* ── HEADER ── */
.cdrawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}
.cdrawer-title {
  display: flex; align-items: center; gap: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 16px; font-weight: 700; color: #fff;
}
.cdrawer-count {
  background: #0078F2; color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 800;
  padding: 2px 7px; border-radius: 20px;
  line-height: 1.5;
}
.cdrawer-close {
  width: 32px; height: 32px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px; cursor: pointer; color: #a3a3a3;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.cdrawer-close:hover { background: rgba(255,255,255,0.1); color: #fff; }

/* ── BODY (scrollable) ── */
.cdrawer-body {
  flex: 1; overflow-y: auto; padding: 16px;
  scrollbar-width: thin; scrollbar-color: #1a1a1a transparent;
}
.cdrawer-body::-webkit-scrollbar { width: 4px; }
.cdrawer-body::-webkit-scrollbar-thumb { background: #1a1a1a; border-radius: 4px; }

/* ── EMPTY STATE ── */
.cdrawer-empty {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  height: 100%; gap: 12px;
  padding: 60px 0; color: #444;
  font-family: 'Inter', sans-serif;
}
.cdrawer-empty-icon { font-size: 52px; margin-bottom: 4px; }
.cdrawer-empty-title { font-size: 15px; font-weight: 600; color: #666; }
.cdrawer-empty-sub { font-size: 13px; color: #444; text-align: center; max-width: 200px; line-height: 1.5; }
.cdrawer-shop-btn {
  margin-top: 8px;
  font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700;
  color: #fff; background: #0078F2; border: none; border-radius: 8px;
  padding: 10px 22px; cursor: pointer; text-decoration: none;
  transition: background 0.15s; display: inline-flex; align-items: center; gap: 6px;
}
.cdrawer-shop-btn:hover { background: #0a84ff; }

/* ── ITEM ── */
.cdrawer-item {
  display: flex; align-items: center; gap: 12px;
  padding: 12px; border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.05);
  background: #161616; margin-bottom: 8px;
  transition: border-color 0.15s;
}
.cdrawer-item:hover { border-color: rgba(255,255,255,0.1); }

.cdrawer-item-img {
  width: 60px; height: 60px; border-radius: 8px;
  object-fit: cover; background: #2a2a2a; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; font-size: 24px;
  overflow: hidden;
}
.cdrawer-item-img img { width: 100%; height: 100%; object-fit: cover; }

.cdrawer-item-info { flex: 1; min-width: 0; }
.cdrawer-item-genre {
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 600; color: #0078F2;
  text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 3px;
}
.cdrawer-item-title {
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 600; color: #fff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 4px;
}
.cdrawer-item-price {
  font-family: 'Inter', sans-serif;
  font-size: 15px; font-weight: 800; color: #0078F2;
}

.cdrawer-item-remove {
  background: none; border: none; cursor: pointer;
  color: #444; padding: 6px; border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s; flex-shrink: 0;
}
.cdrawer-item-remove:hover { color: #ef4444; background: rgba(239,68,68,0.08); }

/* ── FOOTER ── */
.cdrawer-foot {
  border-top: 1px solid rgba(255,255,255,0.06);
  padding: 20px 24px; flex-shrink: 0;
  background: #0f0f0f;
}

.cdrawer-subtotal {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 6px;
  font-family: 'Inter', sans-serif; font-size: 13px; color: #6b6b6b;
}
.cdrawer-total {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 20px;
  font-family: 'Inter', sans-serif;
}
.cdrawer-total-label { font-size: 15px; font-weight: 700; color: #fff; }
.cdrawer-total-amount { font-size: 22px; font-weight: 900; color: #0078F2; letter-spacing: -0.5px; }

.cdrawer-checkout-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%;
  font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 700;
  color: #fff; background: #0078F2; border: none; border-radius: 10px;
  padding: 14px; cursor: pointer; text-decoration: none;
  transition: all 0.15s; margin-bottom: 10px;
}
.cdrawer-checkout-btn:hover { background: #0a84ff; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(0,120,242,0.3); }

.cdrawer-viewcart-btn {
  display: flex; align-items: center; justify-content: center;
  width: 100%;
  font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
  color: #6b6b6b; background: none;
  border: 1px solid rgba(255,255,255,0.08); border-radius: 10px;
  padding: 11px; cursor: pointer; text-decoration: none;
  transition: all 0.15s;
}
.cdrawer-viewcart-btn:hover { color: #fff; border-color: rgba(255,255,255,0.15); }

.cdrawer-note {
  margin-top: 14px;
  font-family: 'Inter', sans-serif; font-size: 11px; color: #3a3a3a;
  text-align: center; line-height: 1.5;
}

/* ── ITEM ENTER ANIMATION ── */
@keyframes cdItemIn {
  from { opacity: 0; transform: translateX(16px); }
  to   { opacity: 1; transform: translateX(0); }
}
.cdrawer-item { animation: cdItemIn 0.2s ease both; }
`;

export default function CartDrawer({ open, onClose }) {
  const { items, removeFromCart, cartCount, cartTotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  function handleCheckout() {
    onClose();
    if (!user) { navigate("/login"); return; }
    if (items.length > 0) navigate(`/checkout/${items[0].id}`);
  }

  function handleViewCart() {
    onClose();
    navigate("/cart");
  }

  return (
    <>
      <style>{CSS}</style>

      {/* Backdrop */}
      <div
        className={`cdrawer-overlay ${open ? "open" : ""}`}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div className={`cdrawer ${open ? "open" : ""}`} role="dialog" aria-label="Shopping cart">

        {/* Header */}
        <div className="cdrawer-head">
          <div className="cdrawer-title">
            <IconCart />
            My Cart
            {cartCount > 0 && <span className="cdrawer-count">{cartCount}</span>}
          </div>
          <button className="cdrawer-close" onClick={onClose} aria-label="Close cart">
            <IconX />
          </button>
        </div>

        {/* Body */}
        <div className="cdrawer-body">
          {cartCount === 0 ? (
            <div className="cdrawer-empty">
              <span className="cdrawer-empty-icon">🛒</span>
              <div className="cdrawer-empty-title">Your cart is empty</div>
              <div className="cdrawer-empty-sub">Add some games and they'll appear here</div>
              <Link to="/store" className="cdrawer-shop-btn" onClick={onClose}>
                Browse Store <IconArrow />
              </Link>
            </div>
          ) : (
            items.map((game, i) => (
              <div
                key={game.id}
                className="cdrawer-item"
                style={{ animationDelay: `${i * 0.04}s` }}
              >
                <div className="cdrawer-item-img">
                  {game.coverImage
                    ? <img src={game.coverImage} alt={game.title} />
                    : "🎮"
                  }
                </div>
                <div className="cdrawer-item-info">
                  {game.genre && <div className="cdrawer-item-genre">{game.genre}</div>}
                  <div className="cdrawer-item-title">{game.title}</div>
                  <div className="cdrawer-item-price">${game.price?.toFixed(2)}</div>
                </div>
                <button
                  className="cdrawer-item-remove"
                  onClick={() => removeFromCart(game.id)}
                  aria-label={`Remove ${game.title}`}
                >
                  <IconTrash />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer — only show when cart has items */}
        {cartCount > 0 && (
          <div className="cdrawer-foot">
            <div className="cdrawer-subtotal">
              <span>{cartCount} item{cartCount > 1 ? "s" : ""}</span>
              <span>${cartTotal.toFixed(2)}</span>
            </div>
            <div className="cdrawer-total">
              <span className="cdrawer-total-label">Total</span>
              <span className="cdrawer-total-amount">${cartTotal.toFixed(2)}</span>
            </div>

            <button className="cdrawer-checkout-btn" onClick={handleCheckout}>
              {user ? "Proceed to Checkout" : "Login to Checkout"} <IconArrow />
            </button>

            <button className="cdrawer-viewcart-btn" onClick={handleViewCart}>
              View Full Cart
            </button>

            <div className="cdrawer-note">
              💳 Secure payment via ABA Pay QR
            </div>
          </div>
        )}
      </div>
    </>
  );
}